import { Component, Input, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { uniq } from 'lodash-es';
import { StatisticService } from '../../../_services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TrainingDataAnalysisModalComponent } from '../data-analysis/_component/data-analysis-modal.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'turnover-month',
    templateUrl: 'floating-turnover.component.html',
})
export class FloatingTurnoverComponent implements OnInit {
    private _fromDate: string;
    private _toDate: string;

    monthStatistics: {};
    monthArrayStatistics: {};
    totalStatistics: { value: any } = { value: null };

    months: any[];

    displayCanvas = false;

    propertyPath = '';

    total: any = {
        value: 0,
        delta: 0,
        percentDelta: 0,
    };

    chart: any = {
        data: [
            { data: [], label: 'Année précédente' },
            { data: [], label: 'Année courante' },
        ],
        labels: [],
        type: 'bar',
        legends: true,
        options: {
            scaleShowVerticalLines: false,
            responsive: true,
            scales: {
                yAxes: [
                    {
                        ticks: {
                            beginAtZero: true,
                            callback: function (value, index, values) {
                                return value
                                    .toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                            },
                        },
                    },
                ],
            },
            tooltips: {
                callbacks: {
                    label: function (item, data) {
                        const total = data.datasets[
                            item.datasetIndex
                        ].data.reduce((total, d) => total + d, 0);

                        let value =
                            data.datasets[item.datasetIndex].data[item.index];
                        const percent = ((value * 100) / total).toFixed(1);
                        value = value
                            .toFixed(0)
                            .replace(/(\d)(?=(\d{3})+\.)/g, '$1,');

                        function replaceAll(str, find, replace) {
                            return str.replace(new RegExp(find, 'g'), replace);
                        }

                        value = replaceAll(value, ',', ' ');
                        value = replaceAll(value, '\\.', ',');

                        return value + ' (' + percent + '%)';
                    },
                },
            },
        },
    };

    dataFilter = 'turnover';
    dataRegion = 'all';
    dataType = 'all';

    // modal datas

    @ViewChild('infoModal')
    infoModal: TrainingDataAnalysisModalComponent;

    modalData = {
        value: {},
    };

    details: any[] = [];

    regions = [];

    translations: {};

    constructor(
        private statService: StatisticService,
        private modalService: NgbModal,
        private translateService: TranslateService
    ) {}

    @Input()
    set fromDate(date: string) {
        this._fromDate = date;
        this.getTurnoverByYearAndMonth(this._fromDate, this._toDate);
    }

    get fromDate() {
        return this._fromDate;
    }

    @Input()
    set toDate(date: string) {
        this._toDate = date;
        this.getTurnoverByYearAndMonth(this._fromDate, this._toDate);
    }

    get toDate() {
        return this._toDate;
    }

    ngOnInit(): void {
        this._fromDate = moment().startOf('year').format('YYYY-MM-DD');

        this._toDate = moment()
            .startOf('month')
            .subtract(1, 'days')
            .format('YYYY-MM-DD');

        this.translations = this.translateService.instant('turnover-month');
        this.getTurnoverByYearAndMonth(this._fromDate, this._toDate);
    }

    getTurnoverByYearAndMonth(from, to) {
        this.statService
            .getTurnoverStatisticsDuringMonth(from, to, false)
            .subscribe((res) => {
                this.monthStatistics = res.months;
                this.totalStatistics.value = res.total;
                this.updateStats();
            });
    }

    updateData(datas: any) {
        const properties = this.buildPropertiesPath();
        this.propertyPath = this.buildPropertiesPath(false).join('.');

        this.months = Object.keys(datas).sort();
        this.chart.data[0].data.length = 0;
        this.chart.data[1].data.length = 0;
        this.chart.labels.length = 0;
        this.total = {};
        this.monthArrayStatistics = {};
        const regions = [];

        this.months.forEach((month) => {
            regions.push(...Object.keys(datas[month].current.locations));
            regions.push(...Object.keys(datas[month].previous.locations));
            this.chart.data[1].data.push(
                this.findFormattedValue(datas[month].current, properties)
            );
            this.chart.data[0].data.push(
                this.findFormattedValue(datas[month].previous, properties)
            );
            this.chart.labels.push(this.formatMonth(month));

            this.monthArrayStatistics[month] = {
                value: {
                    current: datas[month].current,
                    previous: datas[month].previous,
                },
            };
        });

        this.regions = uniq(regions).sort((region1, region2) => {
            if (region1 === 'presta' && region2 === 'autre') {
                return 1;
            }
            if (region1 === 'autre' && region2 === 'presta') {
                return -1;
            }
            if (region1 === 'presta') {
                return 1;
            }
            if (region1 === 'autre') {
                return 1;
            }
            if (region2 === 'presta') {
                return -1;
            }
            if (region2 === 'autre') {
                return -1;
            }
            return region1.localeCompare(region2);
        });

        this.updateTotal(
            this.findFormattedValue(
                this.totalStatistics.value.current,
                properties
            ),
            this.findFormattedValue(
                this.totalStatistics.value.previous,
                properties
            )
        );
    }

    private buildPropertiesPath(withDataType = true): string[] {
        let property = '';
        if (this.dataRegion !== 'all') {
            property += 'locations.' + this.dataRegion + '.';
        }
        if (this.dataType !== 'all') {
            property += 'types.' + this.dataType + '.';
        }
        if (withDataType) {
            property += this.dataFilter;
        }

        return property.split('.');
    }

    private findFormattedValue(data, properties) {
        if (this.dataFilter === 'turnover') {
            return this.findValue(data, properties) / 100;
        }
        return this.findValue(data, properties);
    }

    private findValue(data, properties) {
        let value = data;
        for (let i = 0; i < properties.length; i++) {
            const p = properties[i];
            if (value == null || !value.hasOwnProperty(p)) {
                return 0;
            } else {
                value = value[p];
            }
        }
        return value;
    }

    private formatMonth(month): string {
        const rowMonth = month.split('-')[1];
        return moment(rowMonth, 'MM').format('MMMM');
    }

    formatNumbers(number) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    updateTotal(currentCount, previousCount) {
        this.total.value = this.formatNumbers(currentCount);
        this.total.delta = this.formatNumbers(
            Math.round(currentCount - previousCount)
        );
        this.total.percentDelta = this.formatNumbers(
            Math.round(((currentCount - previousCount) / previousCount) * 100)
        );
    }

    updateStats() {
        this.displayCanvas = false;
        this.updateData(this.monthStatistics);
        setTimeout((_) => (this.displayCanvas = true), 5);
    }

    openInfoModal(data) {
        this.modalData.value =
            this.monthStatistics[this.months[data.active[0]._index]];
        this.infoModal.display(
            'Détail du mois de ' + data.active[0]._model.label,
            this.modalData
        );
    }

    formatDate(stringDate): string {
        return moment(stringDate).format('MMM YY');
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/stats/floating-turnover/floating-turnover.component.ts